import { httpClient } from './httpClient';
import { CommentAttachment } from '../types/comment';

export interface UploadedFile {
  id: number;
  ticket_id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  created_at: string;
  created_by: number;
}

export interface UploadProgress {
  fileName: string;
  progress: number;
}

export const uploadService = {
  async uploadFiles(
    ticketId: number,
    files: File[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFile[]> {
    if (!files.length) return [];
    
    // Check if any file exceeds 50MB (server limit)
    const maxSizeBytes = 50 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      throw new Error(`Files too large: ${fileNames}. Maximum size is 50MB per file.`);
    }
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      // Upload with explicit timeout setting
      const response = await httpClient.post<UploadedFile[]>(`/api/attachments/tickets/${ticketId}`, formData, {
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            // Since we can't track individual files, we'll show overall progress
            files.forEach(file => {
              onProgress({
                fileName: file.name,
                progress,
              });
            });
          }
        },
        // Add explicit headers to help with CORS
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        }
      });

      return response;
    } catch (error) {
      console.error('Error uploading files:', error);
      
      // Provide more detailed error information
      let errorMessage = 'Failed to upload files';
      
      if (error instanceof Error) {
        errorMessage = `Failed to upload files: ${error.message}`;
        
        // Check for specific error types
        if (error.message.includes('413')) {
          errorMessage = 'Files too large. The server rejected the upload. Please try smaller files or fewer files at once.';
        } else if (error.message.includes('Network error')) {
          errorMessage = 'Network error during upload. Please check your connection and try again.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error. The server is not configured to accept uploads from this domain.';
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  async getTicketAttachments(ticketId: number): Promise<UploadedFile[]> {
    try {
      return await httpClient.get<UploadedFile[]>(`/api/attachments/tickets/${ticketId}`);
    } catch (error) {
      console.error('Error getting attachments:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get attachments: ${error.message}`
          : 'Failed to get attachments'
      );
    }
  },

  async deleteAttachment(attachmentId: number): Promise<void> {
    try {
      await httpClient.delete(`/api/attachments/${attachmentId}`);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to delete attachment: ${error.message}`
          : 'Failed to delete attachment'
      );
    }
  },


  /*
  * Comment related methods
  */

  async uploadCommentFiles(
    ticketId: number,
    files: File[],
    commentId: number,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<CommentAttachment[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return httpClient.post<CommentAttachment[]>(`/api/attachments/tickets/${ticketId}/comments/${commentId}`, formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Since we can't track individual files, we'll show overall progress
          files.forEach(file => {
            onProgress({
              fileName: file.name,
              progress,
            });
          });
        }
      },
    });
  },
};
